/**
 * Контрольный тест и отложенная проверка сохранения навыка.
 *
 * Срок отложенной проверки определяет сервер: он единственный знает, когда
 * был засчитан прошлый результат (ТЗ §1.3, FR-VT-01).
 */

import { useCallback, useEffect, useState } from 'react';
import type { LanguageCode } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import type { IntegritySignals } from '@type2x/adapter-web';
import {
  buildControlPayload,
  buildControlTask,
  submitControlResult,
  type ControlSubmission,
  type ControlTask,
} from '@type2x/feature-control-test';

import type { Runtime } from '../runtime.js';

/** Возможности контрольного теста, доступные приложению. */
export interface ControlTestControls {
  /** Наступил ли срок отложенной проверки. */
  readonly retentionDue: boolean;
  /** Готовит задание: отложенная проверка, если её срок наступил. */
  readonly buildTask: (language: LanguageCode, layoutId: ControlTask['layoutId']) => ControlTask;
  /**
   * Отправляет результат и возвращает названный исход: вердикт сервера либо
   * причину, по которой вердикта нет.
   */
  readonly submit: (
    task: ControlTask,
    session: TypingSession,
    signals: IntegritySignals,
  ) => Promise<ControlSubmission>;
  /** Перечитывает срок отложенной проверки. */
  readonly refresh: () => Promise<void>;
}

export function useControlTest(runtime: Runtime, language: LanguageCode): ControlTestControls {
  const [retentionDue, setRetentionDue] = useState(false);

  const refresh = useCallback(async () => {
    const status = await runtime.controlTests.retentionStatus(language);
    // Недоступность сервера не должна показывать отложенную проверку как
    // наступившую: у гостя её просто нет.
    setRetentionDue(status?.due ?? false);
  }, [runtime, language]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const buildTask = useCallback(
    (taskLanguage: LanguageCode, layoutId: ControlTask['layoutId']) =>
      buildControlTask(
        taskLanguage,
        layoutId,
        `${runtime.installationId}:${runtime.clock.epochMs()}`,
        retentionDue,
      ),
    [runtime, retentionDue],
  );

  const submit = useCallback(
    async (
      task: ControlTask,
      session: TypingSession,
      signals: IntegritySignals,
    ): Promise<ControlSubmission> => {
      const payload = buildControlPayload(task, runtime.ids.newSyncId(), session.metrics, {
        pasteDetected: signals.pasteDetected,
        syntheticEvents: signals.syntheticEvents,
        focusLossCount: signals.focusLossCount,
        intervalVariation: signals.intervalVariation,
        minIntervalMs: signals.minIntervalMs,
        identicalIntervalRatio: signals.identicalIntervalRatio,
        windowFocused: true,
      });

      const outcome = await submitControlResult(runtime.controlTests, payload);
      await refresh();

      if (outcome.sent) return { kind: 'verdict', verdict: outcome.verdict };

      // Недостижение требований — предсказуемый исход, а не отказ сервера: он
      // известен до отправки и показывается тем же вердиктом с причиной.
      if (outcome.reason === 'below-requirements') {
        return {
          kind: 'verdict',
          verdict: {
            accepted: false,
            reasons: ['below_minimum_requirements'],
            rating: null,
            retentionCounted: false,
            retentionScore: 0,
          },
        };
      }

      // Остальные причины пришли от шлюза и называют то, что он видел.
      return { kind: outcome.reason };
    },
    [runtime, refresh],
  );

  return { retentionDue, buildTask, submit, refresh };
}
